class ProductSpecification {
  constructor(section) {
    this.section = section;
    this.desktopCarousel = section.querySelector('.spec-carousel-desktop');
    this.mobileCarousel = section.querySelector('.spec-carousel-mobile');
    this.prevBtn = section.querySelector('.spec-arrow-prev');
    this.nextBtn = section.querySelector('.spec-arrow-next');
    this.desktopBlocks = section.querySelectorAll('.spec-block');
    this.mobileBlocks = section.querySelectorAll('.spec-block-mobile');
    
    this.init();
  }

  init() {
    this.checkDesktopLayout();
    this.setupDesktopCarousel();
    this.setupMobileCarousel();
    this.setupNavigationButtons();
    this.updateArrowVisibility();
    
    // Recalculate on window resize
    window.addEventListener('resize', () => {
      this.checkDesktopLayout();
      this.updateArrowVisibility();
    });
  }

  checkDesktopLayout() {
    if (!this.desktopCarousel) return;
    
    const blockCount = this.desktopBlocks.length;
    const wrapper = this.section.querySelector('.spec-carousel-wrapper-desktop');

    // If 6 or fewer blocks, fit them all in the container
    if (blockCount <= 6) {
      this.desktopCarousel.classList.add('fit-container');
      if (wrapper) wrapper.classList.add('fit-container');
    } else {
      this.desktopCarousel.classList.remove('fit-container');
      if (wrapper) wrapper.classList.remove('fit-container');
    }
  }

  setupDesktopCarousel() {
    if (!this.desktopCarousel) return;

    this.desktopBlocks.forEach((block, index) => {
      block.addEventListener('click', () => {
        this.activateDesktopBlock(index);
      });
    });

    // Scroll active block into view on load
    const activeIndex = parseInt(this.desktopCarousel.dataset.activeIndex) || 0;
    setTimeout(() => {
      this.scrollToDesktopBlock(activeIndex);
    }, 100);

    // Update arrow visibility on scroll
    if (this.desktopCarousel) {
      this.desktopCarousel.addEventListener('scroll', () => {
        this.updateArrowVisibility();
      });
    }
  }

  setupMobileCarousel() {
    if (!this.mobileCarousel) return;

    this.mobileBlocks.forEach((block, index) => {
      block.addEventListener('click', () => {
        this.activateMobileBlock(index);
      });
    });

    // Scroll active block into view on load
    const activeIndex = parseInt(this.mobileCarousel.dataset.activeIndex) || 0;
    setTimeout(() => {
      this.scrollToMobileBlock(activeIndex);
    }, 100);
  }

  setupNavigationButtons() {
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => {
        this.scrollDesktopCarousel(-1);
      });
    }

    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => {
        this.scrollDesktopCarousel(1);
      });
    }
  }

  updateArrowVisibility() {
    if (!this.desktopCarousel || !this.prevBtn || !this.nextBtn) return;

    const blockCount = this.desktopBlocks.length;
    
    // Hide arrows if 8 or fewer blocks
    if (blockCount <= 6) {
      this.prevBtn.style.display = 'none';
      this.nextBtn.style.display = 'none';
      return;
    }
    
    // Show arrows and update their state
    this.prevBtn.style.display = 'flex';
    this.nextBtn.style.display = 'flex';

    const { scrollLeft, scrollWidth, clientWidth } = this.desktopCarousel;
    const isAtStart = scrollLeft <= 10;
    const isAtEnd = scrollLeft + clientWidth >= scrollWidth - 10;

    // Disable/enable prev button
    if (isAtStart) {
      this.prevBtn.disabled = true;
      this.prevBtn.style.opacity = '0.3';
    } else {
      this.prevBtn.disabled = false;
      this.prevBtn.style.opacity = '1';
    }

    // Disable/enable next button
    if (isAtEnd) {
      this.nextBtn.disabled = true;
      this.nextBtn.style.opacity = '0.3';
    } else {
      this.nextBtn.disabled = false;
      this.nextBtn.style.opacity = '1';
    }
  }

  activateDesktopBlock(index) {
    // Remove active class from all blocks
    this.desktopBlocks.forEach(block => {
      block.classList.remove('active');
    });

    // Add active class to clicked block
    this.desktopBlocks[index].classList.add('active');

    // Scroll to show the active block properly
    setTimeout(() => {
      this.scrollToDesktopBlock(index);
    }, 100);
  }

  activateMobileBlock(index) {
    // Remove active class from all blocks
    this.mobileBlocks.forEach(block => {
      block.classList.remove('active');
    });

    // Add active class to clicked block
    this.mobileBlocks[index].classList.add('active');

    // Scroll to show the active block properly
    setTimeout(() => {
      this.scrollToMobileBlock(index);
    }, 100);
  }

  scrollToDesktopBlock(index) {
    if (!this.desktopCarousel || !this.desktopBlocks[index]) return;

    const blockCount = this.desktopBlocks.length;
    
    // If blocks fit in container, no need to scroll
    if (blockCount <= 6) return;

    const block = this.desktopBlocks[index];
    const carousel = this.desktopCarousel;
    
    // Get the gap value from computed styles
    const gap = parseInt(window.getComputedStyle(carousel).gap) || 0;
    
    // Calculate the center position
    const carouselCenter = carousel.offsetWidth / 2;
    const blockLeft = block.offsetLeft;
    const blockWidth = block.offsetWidth;
    const blockCenter = blockLeft + (blockWidth / 2);
    
    // Calculate scroll position to center the block
    const scrollLeft = blockCenter - carouselCenter;
    
    carousel.scrollTo({
      left: Math.max(0, scrollLeft),
      behavior: 'smooth'
    });

    // Update arrow visibility after scroll
    setTimeout(() => {
      this.updateArrowVisibility();
    }, 600);
  }

  scrollToMobileBlock(index) {
    if (!this.mobileCarousel || !this.mobileBlocks[index]) return;

    const block = this.mobileBlocks[index];
    const carousel = this.mobileCarousel;
    
    // Calculate the center position
    const carouselCenter = carousel.offsetHeight / 2;
    const blockTop = block.offsetTop;
    const blockHeight = block.offsetHeight;
    const blockCenter = blockTop + (blockHeight / 2);
    
    // Calculate scroll position to center the block
    const scrollTop = blockCenter - carouselCenter;
    
    carousel.scrollTo({
      top: Math.max(0, scrollTop),
      behavior: 'smooth'
    });
  }

  scrollDesktopCarousel(direction) {
    if (!this.desktopCarousel) return;

    const carousel = this.desktopCarousel;
    const scrollAmount = carousel.offsetWidth * 0.7;
    
    carousel.scrollBy({
      left: scrollAmount * direction,
      behavior: 'smooth'
    });

    // Update arrow visibility after scroll animation
    setTimeout(() => {
      this.updateArrowVisibility();
    }, 600);
  }
}

// Initialize all product specification sections
document.addEventListener('DOMContentLoaded', () => {
  const sections = document.querySelectorAll('.product-specification-section');
  sections.forEach(section => {
    new ProductSpecification(section);
  });
});

// Reinitialize on Shopify theme editor events
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.querySelector('.product-specification-section');
    if (section) {
      new ProductSpecification(section);
    }
  });
}